import Vue from "vue";
import Router from "vue-router";
import Splash from "@/layouts/Splash";
import AuthLayout from 'bh-mod/layouts/Auth'
import store from "@/store";
import { VueAppMiddleware } from "bh-mod";
import AppLayout from "bh-mod/layouts/App";

Vue.use(Router);

const router = new Router({
	base: process.env.BASE_URL,
	mode: "history",
	scrollBehavior() {
		return { x: 0, y: 0 };
	},
	routes: [
		{
			path: "/",
			redirect: "/v3",
			component: AppLayout,
			meta: { instanceRequired: true, auth: true, hidden: true },
			children: [
				// Dashboards
				{
					path: "/",
					meta: {
						title: "Exterior Color Packages",
						sidebar: true,
						dataRequired: true,
						lowrise: true,
					},
					component: () => import("./views/app"),
				},
				{
					path: "/addBrick",
					meta: {
						title: "Exterior Color Packages",
						sidebar: true,
						dataRequired: true,
						lowrise: true,
					},
					component: () => import("./views/app/newBrick"),
				},
				{
					path: "/editBrick/:id",
					meta: {
						title: "Exterior Color Packages",
						sidebar: true,
						dataRequired: true,
						lowrise: true,
					},
					component: () => import("./views/app/newBrick"),
				},
				{
					path: "/features",
					meta: {
						title: "Features & Finishes",
						sidebar: true,
						dataRequired: true,
					},
					component: () => import("./views/app/features"),
				},
				{
					path: "/addFeatures",
					meta: {
						title: "Features & Finishes",
						sidebar: true,
						dataRequired: true,
					},
					component: () => import("./views/app/newFeatures"),
				},
				{
					path: "/editFeatures/:id",
					meta: {
						title: "Features & Finishes",
						sidebar: true,
						dataRequired: true,
					},
					component: () => import("./views/app/newFeatures"),
				},
				{
					path: "/categorySettings",
					meta: {
						title: "Category Settings",
						sidebar: true,
						dataRequired: true,
					},
					component: () => import("./views/app/categories"),
				},
				{
					path: "/buildingAmenities",
					meta: {
						title: "Building Amenities",
						sidebar: true,
						dataRequired: true,
						highrise: true,
					},
					component: () => import("./views/app/buildingAmenities"),
				},
				{
					path: "/addBuildingAmenity",
					meta: {
						title: "Add New Building Amenity",
						sidebar: true,
						dataRequired: true,
						highrise: true,
					},
					component: () => import("./views/app/newBuildingAmenity"),
				},
				{
					path: "/editBuildingAmenity/:id",
					meta: {
						title: "Edit Building Amenity",
						sidebar: true,
						dataRequired: true,
						highrise: true,
					},
					component: () => import("./views/app/newBuildingAmenity"),
				},
				{
					path: "/buildingImages",
					meta: {
						title: "Building Images",
						sidebar: true,
						dataRequired: true,
						highrise: true,
					},
					component: () => import("./views/app/buildingImages"),
				},
				{
					path: "/addBuildingImage",
					meta: {
						title: "Add New Building Image",
						sidebar: true,
						dataRequired: true,
						highrise: true,
					},
					component: () => import("./views/app/newBuildingImage"),
				},
				{
					path: "/editBuildingImage/:id",
					meta: {
						title: "Edit Building Image",
						sidebar: true,
						dataRequired: true,
						highrise: true,
					},
					component: () => import("./views/app/newBuildingImage"),
				},
				{
					path: "/tasks-dashboard",
					meta: {
						title: "Tasks Dashboard",
						sidebar: true,
						dataRequired: true
					},
					component: () => import("bh-mod/components/layout/TopBar/TasksDashboard/allTasks.vue"),
				},
				{
					path: "/notifications",
					meta: {
						title: "All Notifications",
						sidebar: true,
						dataRequired: true,
					},
					component: () => import('bh-mod/components/layout/TopBar/Notifications/notifications.vue'),
				},
			],
		},
		{
			path: "/setup",
			component: Splash,
			meta: { hidden: true, splash: true },
			children: [
				{
					path: "/setup",
					meta: {
						title: "Setup Features & Finishes",
						splashScreen: true,
					},
					component: () => import("./views/setup"),
				},
			],
		},
		{
			path: '/404',
			component: Splash,
			children: [
				{
					path: '/404',
					meta: {
						title: 'Error 404',
					},
					component: () => import('bh-mod/components/common/404.vue'),
				}
			]
		},
		{
			path: '/unauthorized',
			component: AuthLayout,
			children: [
				{
					path: '/unauthorized',
					meta: {
						title: 'Unauthorized',
						unauthorized: true
					},
					component: () => import('bh-mod/components/common/unauthorized.vue'),
				}
			]
		},
		// Other routes...
		{
			// Catch-all route: This will match any invalid route
			path: '*',
			redirect: '/404'
		}
	],
});

let hiveRequested = false
router.beforeEach(async (to, from, next) => {
	let dataRequired = to.matched.some((record) => record.meta.dataRequired);
	let splashScreen = to.matched.some((record) => record.meta.splashScreen);
	let unauthorized = to.matched.some(record => record.meta.unauthorized)
	let highrise = to.matched.some(record => record.meta.highrise)
	let lowrise = to.matched.some(record => record.meta.lowrise)
	let error = false

	if (!hiveRequested) {
		let response = await VueAppMiddleware(to, store, 'features')
		error = response.parsed
		hiveRequested = true;
	}

	if (error) {
		if (error === 'User does not have required permission!') {
			return next('/unauthorized')
		}
		return console.error('ERROR', error)
	} else {
		if (unauthorized) {
			return next();
		}

		const isHighrise = store.state.instance.productType === 'highrise';
		const isLowrise = store.state.instance.productType === 'lowrise';
		if (highrise && !isHighrise) {
			return next(isLowrise ? '/' : '/features')
		}
		if (lowrise && !isLowrise) {
			return next('/features')
		}

		const groups = Object.keys(store.state.features.groups).length
		const features = Object.keys(store.state.features.features).length
		const bricks = Object.keys(store.state.features.bricks).length
		const buildingAmenities = Object.keys(store.state.features.buildingAmenities).length
		const buildingImages = Object.keys(store.state.features.buildingImages).length
		const seenIntro = store.state.features.allSettings?.userApp?.options?.seenIntro
		const getStarted = store.state.features.getStarted

		if (splashScreen && (groups || features || (bricks && isLowrise) || buildingAmenities || buildingImages || seenIntro)) {
			return next(isLowrise ? '/' : '/features')
		}
		if (dataRequired && !groups && !features && (!bricks && isLowrise) && (!buildingAmenities && !buildingImages && isHighrise) && !getStarted && !seenIntro) {
			return next('/setup')
		}

		next()
	}
	next();
});

export default router;
